import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Header from "../../ThemeComponents/Header";
import Footer from "../../ThemeComponents/Footer";
import {NavLink, useLocation, useNavigate, useParams} from "react-router-dom";
import Stepper from "../../ThemeComponents/ReservationPage/Stepper";
import ReservationPanel from "../../ThemeComponents/ReservationPage/ReservationPanel";
import ReservationVehicle from "../../ThemeComponents/ReservationPage/ReservationVehicle";
import ReservationInitLoader from "../../ThemeComponents/ReservationPage/ReservationInitLoader";
import ReservationStepNotice from "../../ThemeComponents/ReservationPage/ReservationStepNotice";
import {getLocationInfo} from "../../network/publicCalls/GETLocationInfo";
import {detailVehicleAPI} from "../../network/publicCalls/GETVehicleDetail";
import {getVehicleAvailabilityAPI} from "../../network/publicCalls/GETVehicleAvailability";
import {buildOccupancyLines, buildUnavailableMessage, resolveBookableVehicle} from "../../config/locationAvailability";
import {DISABLED_CACHE} from "../../config/network";
import {formatPriceNl} from "../../config/toolKit";
import {appendPublicWrapperBaseToSearch} from "../../config/publicWrapperBase";
import demoLogo from "../../assets/images/demo.png";
import Devider from "../../ThemeComponents/Devider";
import RentTypeHeader from "../../ThemeComponents/LocationPage/RentTypeHeader";
import RedBackground from "../../ThemeComponents/RedBackground";
import LocationLogoHeader from "../../ThemeComponents/LocationPage/LocationLogoHeader";
import PriceVatToggle from "../../ThemeComponents/PriceVatToggle";
import {getPriceVatPreference, setPriceVatPreference} from "../../config/priceVatPreference";
import {forceScrollToTop} from "../../config/scrollToTop";
import {sendEvent} from "../../config/analytics";
import {resolveWhiteLabelThemeId} from "../../config/whiteLabelThemes";
import {resolveWhiteLabelSubsidiaryId} from "../../config/subsidiaryId";
import {
    getCategoryOptionDurationInDays,
    getNextHolidayTariffRange,
    getTariffName,
    getTariffType,
    isDateWithinHolidayTariff,
    isHolidayTariff,
    isSameHolidayRule,
    isSaturdayTariff,
    isWeekendTariff
} from "../../config/categoryTariffs";

const DEMO_LOCATION_ID = '875'
const DEFAULT_WEEKEND_START_MINUTES = 8 * 60
const DEFAULT_WEEKEND_END_MINUTES = 8 * 60
const normalizeIncomingDateTime = (value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return ""

    const parsed = new Date(rawValue)
    if (Number.isNaN(parsed.getTime())) return ""

    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, "0")
    const day = String(parsed.getDate()).padStart(2, "0")
    const hours = String(parsed.getHours()).padStart(2, "0")
    const minutes = String(parsed.getMinutes()).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}`
}
const buildInitialReservationStateFromSearch = (search) => {
    const queryParams = new URLSearchParams(search)
    const from = normalizeIncomingDateTime(queryParams.get("from"))
    const to = normalizeIncomingDateTime(queryParams.get("to"))
    const expectedKm = String(queryParams.get("expectedKm") || "").trim()

    return {
        rentalPeriod: {from, to},
        expectedKm,
    }
}
const buildPrivateShareDefaultRentalPeriod = (referenceDate = new Date()) => {
    const start = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate(),
        8,
        0,
        0,
        0
    )
    const end = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 1,
        8,
        0,
        0,
        0
    )

    const formatDateTime = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        const hours = String(date.getHours()).padStart(2, "0")
        const minutes = String(date.getMinutes()).padStart(2, "0")
        return `${year}-${month}-${day}T${hours}:${minutes}`
    }

    return {
        from: formatDateTime(start),
        to: formatDateTime(end),
    }
}

const ReservationInitPage = () => {

    const {vehicleId, locationId} = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const isDemoReservationLocation = String(locationId) === DEMO_LOCATION_ID
    const initialReservationState = useMemo(
        () => buildInitialReservationStateFromSearch(location.search),
        [location.search]
    )

    const [locationInfo, setLocationInfo] = useState(null)
    const [vehicleInfo, setVehicleInfo] = useState(null)
    const initialPeriodSetRef = useRef(
        Boolean(initialReservationState.rentalPeriod.from && initialReservationState.rentalPeriod.to)
    )
    const [showExpectedKmModal, setShowExpectedKmModal] = useState(false)
    const [showPricesInclVat, setShowPricesInclVat] = useState(() => getPriceVatPreference(false))
    const [isAvailabilityChecking, setIsAvailabilityChecking] = useState(false)
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
    const [availabilityModal, setAvailabilityModal] = useState({title: "", intro: "", lines: [], outro: ""})
    const [availabilityInlineMessage, setAvailabilityInlineMessage] = useState("")

    useEffect(() => {
        forceScrollToTop()
    }, [location.pathname])

    useEffect(() => {
        sendEvent("reserve_period_view")
    }, [])

    useEffect(() => {
        if (locationId) {
            (async () => {
                try {
                    const locationResponse = await getLocationInfo({locationId, force: DISABLED_CACHE})
                    setLocationInfo(locationResponse)
                } catch (ex) {

                }

            })()
        }
    }, [locationId]);

    useEffect(() => {
        if (vehicleId) {
            (async () => {
               const vehicleResponse = await detailVehicleAPI({vehicleId, force: DISABLED_CACHE})
               if (vehicleResponse?.white_label_overrides?.rental_config === "privateshare_daily") {
                   setRentalPeriod(buildPrivateShareDefaultRentalPeriod())
                   initialPeriodSetRef.current = true
               }
               setVehicleInfo(vehicleResponse)
            })()
        }
    }, [vehicleId]);

    const [rentalPeriod, setRentalPeriod] = useState(initialReservationState.rentalPeriod)
    const [expectedKm, setExpectedKm] = useState(initialReservationState.expectedKm)
    const [selectedCategoryOptionId, setSelectedCategoryOptionId] = useState(null)
    const [showDailyKmHighlight, setShowDailyKmHighlight] = useState(false)
    const [isDailyCategoryOptionLoading, setIsDailyCategoryOptionLoading] = useState(false)
    const [dailyCategoryOptionLoadingId, setDailyCategoryOptionLoadingId] = useState(null)
    const hasManualCategorySelectionRef = useRef(false)
    const isUpdatingPeriodFromCategoryRef = useRef(false)
    const [pricingMeta, setPricingMeta] = useState({
        specialPriceType: null,
        enabledFor: null,
        specialPriceRule: null,
        isWeekendKb: false,
        isSaturdayKb: false,
        isHoliday: false,
        holidayTariffName: "",
        vehicleRentPrice: null,
        freeKilometers: null,
        expectedKilometers: null,
        pricePerExtraKilometer: null,
        eigenRisk: null,
    })

    const handlePricingMetaChange = useCallback((nextMeta) => {
        const nextSpecialPriceType = String(nextMeta?.specialPriceType || "").trim() || null
        const nextEnabledFor = String(nextMeta?.enabledFor || "").trim() || null
        const nextSpecialPriceRule = nextMeta?.specialPriceRule || null
        const nextIsWeekendKb = Boolean(nextMeta?.isWeekendKb)
        const nextIsSaturdayKb = Boolean(nextMeta?.isSaturdayKb)
        const nextIsHoliday = Boolean(nextMeta?.isHoliday)
        const nextHolidayTariffName = String(nextMeta?.holidayTariffName || "").trim()
        const rawPrice = Number(nextMeta?.vehicleRentPrice)
        const nextVehicleRentPrice = Number.isFinite(rawPrice) ? rawPrice : null
        const rawFreeKilometers = Number(nextMeta?.freeKilometers)
        const nextFreeKilometers = Number.isFinite(rawFreeKilometers) ? rawFreeKilometers : null
        const rawExpectedKilometers = Number(nextMeta?.expectedKilometers)
        const nextExpectedKilometers = Number.isFinite(rawExpectedKilometers) ? rawExpectedKilometers : null
        const rawPricePerExtraKilometer = Number(nextMeta?.pricePerExtraKilometer)
        const nextPricePerExtraKilometer = Number.isFinite(rawPricePerExtraKilometer) ? rawPricePerExtraKilometer : null
        const rawEigenRisk = Number(nextMeta?.eigenRisk)
        const nextEigenRisk = Number.isFinite(rawEigenRisk) ? rawEigenRisk : null

        setPricingMeta((prev) => {
            if (
                prev.specialPriceType === nextSpecialPriceType &&
                prev.enabledFor === nextEnabledFor &&
                prev.specialPriceRule === nextSpecialPriceRule &&
                prev.isWeekendKb === nextIsWeekendKb &&
                prev.isSaturdayKb === nextIsSaturdayKb &&
                prev.isHoliday === nextIsHoliday &&
                prev.holidayTariffName === nextHolidayTariffName &&
                prev.vehicleRentPrice === nextVehicleRentPrice &&
                prev.freeKilometers === nextFreeKilometers &&
                prev.expectedKilometers === nextExpectedKilometers &&
                prev.pricePerExtraKilometer === nextPricePerExtraKilometer &&
                prev.eigenRisk === nextEigenRisk
            ) {
                return prev
            }

            return {
                specialPriceType: nextSpecialPriceType,
                enabledFor: nextEnabledFor,
                specialPriceRule: nextSpecialPriceRule,
                isWeekendKb: nextIsWeekendKb,
                isSaturdayKb: nextIsSaturdayKb,
                isHoliday: nextIsHoliday,
                holidayTariffName: nextHolidayTariffName,
                vehicleRentPrice: nextVehicleRentPrice,
                freeKilometers: nextFreeKilometers,
                expectedKilometers: nextExpectedKilometers,
                pricePerExtraKilometer: nextPricePerExtraKilometer,
                eigenRisk: nextEigenRisk,
            }
        })
    }, [])

    const stripTime = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }
    const minutesToTime = (minutes) => {
        const safeMinutes = Math.max(0, Math.min(24 * 60, minutes))
        const hour = String(Math.floor(safeMinutes / 60)).padStart(2, '0')
        const minute = String(safeMinutes % 60).padStart(2, '0')
        return `${hour}:${minute}`
    }
    const monthNamesLong = [
        "januari",
        "februari",
        "maart",
        "april",
        "mei",
        "juni",
        "juli",
        "augustus",
        "september",
        "oktober",
        "november",
        "december",
    ]
    const formatDateTimeFullNl = (value) => {
        if (!value) return null
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return null
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}-${month}-${year} ${hours}:${minutes}`
    }
    const formatDateTimeShortNl = (value) => {
        if (!value) return null
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return null
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const day = date.getDate()
        const month = monthNamesLong[date.getMonth()]
        return `${hours}:${minutes} (${day} ${month})`
    }
    const normalizeOccupiedItem = (item) => {
        if (!item) return {start: null, end: null}
        if (typeof item === 'string') return {start: item, end: null}
        return {
            start: item.start_date || item.startDate || item.from || item[0] || null,
            end: item.end_date || item.endDate || item.to || item[1] || null,
        }
    }
    const getOccupiedRanges = (occupiedAt) => {
        if (!occupiedAt) return []
        if (Array.isArray(occupiedAt)) {
            if (occupiedAt.length === 0) return []
            const hasObjects = occupiedAt.some((item) => item && typeof item === 'object')
            if (hasObjects) {
                return occupiedAt.map(normalizeOccupiedItem).filter((item) => item.start || item.end)
            }
            return [{start: occupiedAt[0] || null, end: occupiedAt[1] || null}]
        }
        const single = normalizeOccupiedItem(occupiedAt)
        return single.start || single.end ? [single] : []
    }
    const normalizeStep = (step) => {
        const parsed = Number(step)
        if (!Number.isFinite(parsed) || parsed <= 0) return 30
        return Math.round(parsed)
    }
    const roundDownToStep = (minutes, step) => {
        const safeStep = normalizeStep(step)
        return Math.floor(minutes / safeStep) * safeStep
    }
    const roundUpToStep = (minutes, step) => {
        const safeStep = normalizeStep(step)
        return Math.ceil(minutes / safeStep) * safeStep
    }
    const getScheduleEntry = (date, schedule) => {
        if (!schedule || !date) return null
        const dayIndex = date.getDay() === 0 ? 7 : date.getDay()
        return schedule.find((entry) => entry.dayOfWeek === dayIndex) || null
    }
    const getOpenCloseMinutes = (date, schedule) => {
        const entry = getScheduleEntry(date, schedule)
        if (!entry || entry.isClosed) return null
        if (entry.isOpen24h) return {open: 0, close: 24 * 60}
        return {
            open: entry.openHour * 60 + entry.openMinute,
            close: entry.closeHour * 60 + entry.closeMinute
        }
    }
    const ensureWithinSchedule = (dateTime, schedule, step, direction = 'down') => {
        let date = stripTime(dateTime)
        let minutes = dateTime.getHours() * 60 + dateTime.getMinutes()
        for (let attempt = 0; attempt < 14; attempt += 1) {
            const range = getOpenCloseMinutes(date, schedule)
            if (!range) {
                date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
                minutes = 0
                continue
            }
            const alignedMinutes = direction === 'up'
                ? roundUpToStep(minutes, step)
                : roundDownToStep(minutes, step)
            minutes = alignedMinutes
            if (minutes < range.open) minutes = range.open
            if (minutes > range.close) {
                date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
                minutes = 0
                continue
            }
            return {date, minutes}
        }
        return {date, minutes}
    }

    const ensureOpenDay = (dateTime, schedule) => {
        let date = stripTime(dateTime)
        const minutes = dateTime.getHours() * 60 + dateTime.getMinutes()
        for (let attempt = 0; attempt < 14; attempt += 1) {
            const range = getOpenCloseMinutes(date, schedule)
            if (!range) {
                date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
                continue
            }
            return {date, minutes}
        }
        return {date, minutes}
    }

    const isWhiteLabelRentalConfigOverride = useMemo(() => {
        return vehicleInfo?.white_label_overrides?.rental_config === "privateshare_daily"
    }, [vehicleInfo])
    const isPrivateShareTheme = useMemo(() => {
        return resolveWhiteLabelThemeId() === "privateshare"
    }, [])
    const useCurrentTimeDefault = useMemo(() => {
        if (isWhiteLabelRentalConfigOverride) return false
        return vehicleInfo?.rental_config?.minimum_rental_time?.unit === 'hour'
    }, [vehicleInfo, isWhiteLabelRentalConfigOverride])
    const isDailyRental = useMemo(() => {
        if (isWhiteLabelRentalConfigOverride) return true
        return vehicleInfo?.rental_config?.minimum_rental_time?.unit === 'day'
    }, [vehicleInfo, isWhiteLabelRentalConfigOverride])
    const shouldShowExpectedKmField = useMemo(() => {
        if (isPrivateShareTheme) return true
        return vehicleInfo?.rental_config?.minimum_rental_time?.unit !== 'day'
    }, [vehicleInfo, isPrivateShareTheme])
    const dailyCategoryOptions = useMemo(() => {
        return Array.isArray(vehicleInfo?.category_options) ? vehicleInfo.category_options : []
    }, [vehicleInfo?.category_options])
    const cheapestAllWeekDayOption = useMemo(() => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return null

        const allWeekDayOptions = dailyCategoryOptions.filter((option) => {
            const enabledFor = String(option?.enabled_for || "").trim().toLowerCase()
            const durationUnit = String(option?.duration?.unit || "").trim().toLowerCase()
            const durationValue = Number(option?.duration?.value)
            return enabledFor === "all week" && durationUnit === "day" && durationValue === 1
        })

        if (allWeekDayOptions.length === 0) return null

        return allWeekDayOptions
            .slice()
            .sort((left, right) => {
                const leftPrice = Number(left?.price_including_vat)
                const rightPrice = Number(right?.price_including_vat)

                const safeLeftPrice = Number.isFinite(leftPrice) ? leftPrice : Number.POSITIVE_INFINITY
                const safeRightPrice = Number.isFinite(rightPrice) ? rightPrice : Number.POSITIVE_INFINITY

                if (safeLeftPrice !== safeRightPrice) return safeLeftPrice - safeRightPrice

                const leftId = Number(left?.id)
                const rightId = Number(right?.id)
                if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId

                return 0
            })[0]
    }, [dailyCategoryOptions])
    const cheapestExponentialDayOption = useMemo(() => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return null

        const exponentialDayOptions = dailyCategoryOptions.filter((option) => {
            const enabledFor = String(option?.enabled_for || "").trim().toLowerCase()
            const durationUnit = String(option?.duration?.unit || "").trim().toLowerCase()
            const durationValue = Number(option?.duration?.value)
            return enabledFor === "exponential" && durationUnit === "day" && durationValue === 1
        })

        if (exponentialDayOptions.length === 0) return null

        return exponentialDayOptions
            .slice()
            .sort((left, right) => {
                const leftPrice = Number(left?.price_including_vat)
                const rightPrice = Number(right?.price_including_vat)

                const safeLeftPrice = Number.isFinite(leftPrice) ? leftPrice : Number.POSITIVE_INFINITY
                const safeRightPrice = Number.isFinite(rightPrice) ? rightPrice : Number.POSITIVE_INFINITY

                if (safeLeftPrice !== safeRightPrice) return safeLeftPrice - safeRightPrice

                const leftId = Number(left?.id)
                const rightId = Number(right?.id)
                if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId

                return 0
            })[0]
    }, [dailyCategoryOptions])
    const cheapestWeeklyOption = useMemo(() => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return null

        const weeklyOptions = dailyCategoryOptions.filter((option) => {
            const durationUnit = String(option?.duration?.unit || "").trim().toLowerCase()
            const durationValue = Number(option?.duration?.value)
            if (!Number.isFinite(durationValue) || durationValue <= 0) return false

            if (durationUnit === "week") return durationValue >= 1
            if (durationUnit === "day") return durationValue >= 7
            return false
        })

        if (weeklyOptions.length === 0) return null

        return weeklyOptions
            .slice()
            .sort((left, right) => {
                const leftPrice = Number(left?.price_including_vat)
                const rightPrice = Number(right?.price_including_vat)

                const safeLeftPrice = Number.isFinite(leftPrice) ? leftPrice : Number.POSITIVE_INFINITY
                const safeRightPrice = Number.isFinite(rightPrice) ? rightPrice : Number.POSITIVE_INFINITY

                if (safeLeftPrice !== safeRightPrice) return safeLeftPrice - safeRightPrice

                const leftId = Number(left?.id)
                const rightId = Number(right?.id)
                if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId

                return 0
            })[0]
    }, [dailyCategoryOptions])
    const matchedAllWeekOptionForCurrentRange = useMemo(() => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return null
        if (!rentalPeriod.from || !rentalPeriod.to) return null

        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null

        const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
        if (diffMinutes <= 0) return null

        const normalizedRangeDays = Math.max(1, Math.ceil(diffMinutes / (24 * 60)))

        const matchingOptions = dailyCategoryOptions.filter((option) => {
            const enabledFor = String(option?.enabled_for || "").trim().toLowerCase()
            const durationDays = getCategoryOptionDurationInDays(option)
            return enabledFor === "all week" && Number.isFinite(durationDays) && durationDays === normalizedRangeDays
        })

        if (matchingOptions.length === 0) return null

        return matchingOptions
            .slice()
            .sort((left, right) => {
                const leftPrice = Number(left?.price_including_vat)
                const rightPrice = Number(right?.price_including_vat)

                const safeLeftPrice = Number.isFinite(leftPrice) ? leftPrice : Number.POSITIVE_INFINITY
                const safeRightPrice = Number.isFinite(rightPrice) ? rightPrice : Number.POSITIVE_INFINITY

                if (safeLeftPrice !== safeRightPrice) return safeLeftPrice - safeRightPrice

                const leftId = Number(left?.id)
                const rightId = Number(right?.id)
                if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId

                return 0
            })[0]
    }, [dailyCategoryOptions, rentalPeriod.from, rentalPeriod.to])
    const maxSupportedDailyRangeDays = useMemo(() => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return 7

        const allWeekDurations = dailyCategoryOptions
            .filter((option) => String(option?.enabled_for || "").trim().toLowerCase() === "all week")
            .map((option) => getCategoryOptionDurationInDays(option))
            .filter((days) => Number.isFinite(days) && days > 0)

        if (allWeekDurations.length === 0) return 7
        return Math.max(7, ...allWeekDurations)
    }, [dailyCategoryOptions])
    const findHolidayCategoryOptionForCurrentContext = useCallback((specialPriceRule = null) => {
        if (!Array.isArray(dailyCategoryOptions) || dailyCategoryOptions.length === 0) return null

        const holidayOptions = dailyCategoryOptions.filter((option) => isHolidayTariff(option))
        if (holidayOptions.length === 0) return null

        if (specialPriceRule) {
            const byRule = holidayOptions.find((option) => isSameHolidayRule(option, specialPriceRule))
            if (byRule) return byRule

            const ruleName = getTariffName(specialPriceRule)
            if (ruleName) {
                const byName = holidayOptions.find((option) => getTariffName(option) === ruleName)
                if (byName) return byName
            }
        }

        const start = rentalPeriod?.from ? new Date(rentalPeriod.from) : null
        if (start && !Number.isNaN(start.getTime())) {
            const byStartDate = holidayOptions.find((option) => isDateWithinHolidayTariff(start, option))
            if (byStartDate) return byStartDate
        }

        return specialPriceRule ? holidayOptions[0] : null
    }, [dailyCategoryOptions, rentalPeriod?.from])
    const shouldShowDailyCategoryOptions = useMemo(() => {
        return isDailyRental && !isWhiteLabelRentalConfigOverride && dailyCategoryOptions.length > 0
    }, [isDailyRental, isWhiteLabelRentalConfigOverride, dailyCategoryOptions])

    useEffect(() => {
        if (!shouldShowDailyCategoryOptions) {
            if (selectedCategoryOptionId !== null) {
                setSelectedCategoryOptionId(null)
            }
            hasManualCategorySelectionRef.current = false
            return
        }

        const selectedExists = dailyCategoryOptions.some(
            (option) => Number(option?.id) === Number(selectedCategoryOptionId)
        )
        if (selectedExists) return

        const firstOption = cheapestAllWeekDayOption || dailyCategoryOptions[0]
        if (!firstOption) return

        const nextId = Number(firstOption?.id)
        const nextFreeKm = Number(firstOption?.free_km)

        setSelectedCategoryOptionId(Number.isFinite(nextId) ? nextId : null)
        const normalizedFreeKm = Number.isFinite(nextFreeKm) ? String(Math.max(0, Math.round(nextFreeKm))) : ""
        setExpectedKm((prev) => (prev === normalizedFreeKm ? prev : normalizedFreeKm))
    }, [shouldShowDailyCategoryOptions, dailyCategoryOptions, selectedCategoryOptionId, cheapestAllWeekDayOption])

    useEffect(() => {
        if (isUpdatingPeriodFromCategoryRef.current) {
            isUpdatingPeriodFromCategoryRef.current = false
            return
        }
        hasManualCategorySelectionRef.current = false
    }, [rentalPeriod.from, rentalPeriod.to])

    useEffect(() => {
        if (!shouldShowDailyCategoryOptions || !rentalPeriod.from || !rentalPeriod.to) return
        if (!cheapestAllWeekDayOption) return

        const selectedOption = dailyCategoryOptions.find(
            (option) => Number(option?.id) === Number(selectedCategoryOptionId)
        ) || null
        if (!selectedOption) return

        const selectedDurationValue = Number(selectedOption?.duration?.value)
        const selectedDurationUnit = String(selectedOption?.duration?.unit || "").trim().toLowerCase()
        const isSelectedWeeklyOption = (
            (selectedDurationValue === 1 && selectedDurationUnit === "week") ||
            (selectedDurationValue === 7 && selectedDurationUnit === "day")
        )
        if (!isSelectedWeeklyOption) return

        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return

        const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
        const oneWeekMinutes = 7 * 24 * 60
        if (diffMinutes >= oneWeekMinutes) return

        const fallbackId = Number(cheapestAllWeekDayOption?.id)
        if (!Number.isFinite(fallbackId)) return
        if (Number(selectedCategoryOptionId) === fallbackId) return

        const fallbackFreeKm = Number(cheapestAllWeekDayOption?.free_km)
        const fallbackExpectedKm = Number.isFinite(fallbackFreeKm)
            ? String(Math.max(0, Math.round(fallbackFreeKm)))
            : ""

        hasManualCategorySelectionRef.current = false
        setSelectedCategoryOptionId(fallbackId)
        setExpectedKm((prev) => (prev === fallbackExpectedKm ? prev : fallbackExpectedKm))
    }, [
        shouldShowDailyCategoryOptions,
        rentalPeriod.from,
        rentalPeriod.to,
        dailyCategoryOptions,
        selectedCategoryOptionId,
        cheapestAllWeekDayOption,
    ])

    useEffect(() => {
        if (!shouldShowDailyCategoryOptions || !rentalPeriod.from || !rentalPeriod.to) return
        if (!cheapestWeeklyOption) return

        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return

        const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
        const oneWeekMinutes = 7 * 24 * 60
        if (diffMinutes < oneWeekMinutes) return

        const selectedOption = dailyCategoryOptions.find(
            (option) => Number(option?.id) === Number(selectedCategoryOptionId)
        ) || null
        const selectedDurationValue = Number(selectedOption?.duration?.value)
        const selectedDurationUnit = String(selectedOption?.duration?.unit || "").trim().toLowerCase()
        const isSelectedWeeklyOption = (
            (selectedDurationValue === 1 && selectedDurationUnit === "week") ||
            (selectedDurationValue >= 7 && selectedDurationUnit === "day")
        )

        // Keep any currently selected weekly tariff while the range stays 7+ days.
        if (isSelectedWeeklyOption) return

        const fallbackId = Number(cheapestWeeklyOption?.id)
        if (!Number.isFinite(fallbackId)) return
        if (Number(selectedCategoryOptionId) === fallbackId) return

        const fallbackFreeKm = Number(cheapestWeeklyOption?.free_km)
        const fallbackExpectedKm = Number.isFinite(fallbackFreeKm)
            ? String(Math.max(0, Math.round(fallbackFreeKm)))
            : ""

        hasManualCategorySelectionRef.current = false
        setSelectedCategoryOptionId(fallbackId)
        setExpectedKm((prev) => (prev === fallbackExpectedKm ? prev : fallbackExpectedKm))
    }, [
        shouldShowDailyCategoryOptions,
        rentalPeriod.from,
        rentalPeriod.to,
        selectedCategoryOptionId,
        cheapestWeeklyOption,
    ])

    useEffect(() => {
        if (!shouldShowDailyCategoryOptions || dailyCategoryOptions.length === 0) return

        const normalizedEnabledFor = String(pricingMeta?.specialPriceType || pricingMeta?.enabledFor || "").trim()
        const hasManualSelection = hasManualCategorySelectionRef.current
        const hasSpecialPrice = Boolean(String(pricingMeta?.specialPriceType || "").trim())
        const selectedOption = dailyCategoryOptions.find(
            (option) => Number(option?.id) === Number(selectedCategoryOptionId)
        ) || null
        const selectedEnabledFor = getTariffType(selectedOption)
        const normalizedSelectedEnabledFor = selectedEnabledFor.toLowerCase()
        const isSelectedSpecialOption =
            isWeekendTariff(selectedEnabledFor) ||
            isSaturdayTariff(selectedEnabledFor) ||
            isHolidayTariff(selectedEnabledFor)
        const selectedDurationDays = getCategoryOptionDurationInDays(selectedOption)
        const start = rentalPeriod?.from ? new Date(rentalPeriod.from) : null
        const end = rentalPeriod?.to ? new Date(rentalPeriod.to) : null
        const hasValidRange = Boolean(
            start &&
            end &&
            !Number.isNaN(start.getTime()) &&
            !Number.isNaN(end.getTime())
        )
        const rangeMinutes = hasValidRange
            ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
            : 0
        const normalizedRangeDays = hasValidRange
            ? Math.max(1, Math.ceil(rangeMinutes / (24 * 60)))
            : null
        const isAtLeastWeekRange = rangeMinutes >= (7 * 24 * 60)
        const isSelectedAllWeekOptionForCurrentRange = Boolean(
            selectedOption &&
            normalizedSelectedEnabledFor === "all week" &&
            Number.isFinite(selectedDurationDays) &&
            Number.isFinite(normalizedRangeDays) &&
            selectedDurationDays === normalizedRangeDays
        )

        let matchedOption = null

        if (hasManualSelection && !hasSpecialPrice) return

        if (hasSpecialPrice && normalizedEnabledFor) {
            if (isHolidayTariff(normalizedEnabledFor)) {
                matchedOption = findHolidayCategoryOptionForCurrentContext(pricingMeta?.specialPriceRule)
            } else {
                matchedOption = dailyCategoryOptions.find(
                    (option) => getTariffType(option) === normalizedEnabledFor
                ) || null
            }
        } else if (isAtLeastWeekRange) {
            if (matchedAllWeekOptionForCurrentRange && !isSelectedAllWeekOptionForCurrentRange) {
                matchedOption = matchedAllWeekOptionForCurrentRange
            } else if (cheapestWeeklyOption) {
                matchedOption = cheapestWeeklyOption
            }
        }

        if (!hasSpecialPrice && !isAtLeastWeekRange) {
            if (isSelectedSpecialOption && cheapestExponentialDayOption) {
                matchedOption = cheapestExponentialDayOption
            } else if (matchedAllWeekOptionForCurrentRange && !isSelectedAllWeekOptionForCurrentRange) {
                matchedOption = matchedAllWeekOptionForCurrentRange
            } else if (!isSelectedAllWeekOptionForCurrentRange && cheapestAllWeekDayOption) {
                matchedOption = cheapestAllWeekDayOption
            }
        }

        if (!matchedOption) return

        const nextId = Number(matchedOption?.id)
        const nextFreeKm = Number(matchedOption?.free_km)
        const normalizedFreeKm = Number.isFinite(nextFreeKm) ? String(Math.max(0, Math.round(nextFreeKm))) : ""

        if (Number.isFinite(nextId)) {
            setSelectedCategoryOptionId((prev) => (Number(prev) === nextId ? prev : nextId))
        }
        setExpectedKm((prev) => (prev === normalizedFreeKm ? prev : normalizedFreeKm))
    }, [
        shouldShowDailyCategoryOptions,
        dailyCategoryOptions,
        selectedCategoryOptionId,
        cheapestExponentialDayOption,
        cheapestAllWeekDayOption,
        cheapestWeeklyOption,
        matchedAllWeekOptionForCurrentRange,
        pricingMeta?.specialPriceType,
        pricingMeta?.enabledFor,
        pricingMeta?.specialPriceRule,
        findHolidayCategoryOptionForCurrentContext,
        rentalPeriod?.from,
        rentalPeriod?.to,
    ])

    const getManualCategorySelectionStartDate = useCallback(() => {
        const currentStart = rentalPeriod?.from ? new Date(rentalPeriod.from) : null
        if (currentStart && !Number.isNaN(currentStart.getTime())) {
            return stripTime(currentStart)
        }

        return stripTime(new Date())
    }, [rentalPeriod?.from])

    const getStartDateAfterHolidayRanges = useCallback((referenceDate, durationDays = 1) => {
        const holidayOptions = Array.isArray(dailyCategoryOptions)
            ? dailyCategoryOptions.filter((option) => isHolidayTariff(option))
            : []

        let startDate = stripTime(referenceDate) || stripTime(new Date())
        if (holidayOptions.length === 0) return startDate

        const safeDurationDays = Number.isFinite(Number(durationDays)) && Number(durationDays) > 0
            ? Math.max(1, Math.round(Number(durationDays)))
            : 1

        for (let attempt = 0; attempt < 20; attempt += 1) {
            const currentStartDate = startDate
            const lastRentalDate = new Date(
                currentStartDate.getFullYear(),
                currentStartDate.getMonth(),
                currentStartDate.getDate() + safeDurationDays - 1
            )

            const overlappingRanges = holidayOptions
                .map((holidayOption) => getNextHolidayTariffRange(holidayOption, currentStartDate))
                .filter((range) => (
                    range &&
                    range.from.getTime() <= lastRentalDate.getTime() &&
                    range.until.getTime() >= currentStartDate.getTime()
                ))

            if (overlappingRanges.length === 0) return startDate

            const latestHolidayEnd = overlappingRanges.reduce((latest, range) => (
                range.until.getTime() > latest.getTime() ? range.until : latest
            ), overlappingRanges[0].until)

            startDate = new Date(
                latestHolidayEnd.getFullYear(),
                latestHolidayEnd.getMonth(),
                latestHolidayEnd.getDate() + 1
            )
        }

        return startDate
    }, [dailyCategoryOptions])

    const handleDailyCategoryOptionSelect = useCallback((categoryOption) => {
        const nextId = Number(categoryOption?.id)
        const nextFreeKm = Number(categoryOption?.free_km)
        const enabledFor = String(categoryOption?.enabled_for || "").trim()
        const durationValue = Number(categoryOption?.duration?.value)
        const durationUnit = String(categoryOption?.duration?.unit || "").trim().toLowerCase()
        const allWeekDurationDays = enabledFor === "All Week"
            ? getCategoryOptionDurationInDays(categoryOption)
            : null
        const isOneWeekDuration = (
            (durationValue === 1 && durationUnit === "week") ||
            (durationValue === 7 && durationUnit === "day")
        )
        const isAllWeekMultiDayDuration = (
            Number.isFinite(allWeekDurationDays) &&
            allWeekDurationDays > 1 &&
            allWeekDurationDays < 7
        )
        const isAllWeekLongDuration = (
            Number.isFinite(allWeekDurationDays) &&
            allWeekDurationDays > 7
        )

        hasManualCategorySelectionRef.current = true
        setSelectedCategoryOptionId(Number.isFinite(nextId) ? nextId : null)
        const normalizedFreeKm = Number.isFinite(nextFreeKm) ? String(Math.max(0, Math.round(nextFreeKm))) : ""
        const isSameSelection = Number(selectedCategoryOptionId) === nextId
        const isSameExpectedKm = String(expectedKm || "") === normalizedFreeKm
        if (!isSameSelection || !isSameExpectedKm) {
            setDailyCategoryOptionLoadingId(Number.isFinite(nextId) ? nextId : null)
            setIsDailyCategoryOptionLoading(true)
        }
        setExpectedKm((prev) => (prev === normalizedFreeKm ? prev : normalizedFreeKm))

        if (isWeekendTariff(enabledFor)) {
            const now = new Date()
            const weekendStart = new Date(now)
            const daysUntilSaturday = (6 - weekendStart.getDay() + 7) % 7
            weekendStart.setDate(weekendStart.getDate() + daysUntilSaturday)

            const saturdayRange = getOpenCloseMinutes(weekendStart, locationInfo?.schedule)
            const saturdayOpenMinutes = saturdayRange?.open ?? 8 * 60
            weekendStart.setHours(Math.floor(saturdayOpenMinutes / 60), saturdayOpenMinutes % 60, 0, 0)

            if (weekendStart.getTime() <= now.getTime()) {
                weekendStart.setDate(weekendStart.getDate() + 7)
            }

            const weekendEnd = new Date(weekendStart)
            weekendEnd.setDate(weekendEnd.getDate() + 2)
            const mondayRange = getOpenCloseMinutes(weekendEnd, locationInfo?.schedule)
            const mondayOpenMinutes = mondayRange?.open ?? 8 * 60
            weekendEnd.setHours(Math.floor(mondayOpenMinutes / 60), mondayOpenMinutes % 60, 0, 0)

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod({
                from: `${formatDate(weekendStart)}T${minutesToTime(saturdayOpenMinutes)}`,
                to: `${formatDate(weekendEnd)}T${minutesToTime(mondayOpenMinutes)}`
            })
            return
        }
        if (isSaturdayTariff(enabledFor)) {
            const now = new Date()
            const saturdayStart = new Date(now)
            const daysUntilSaturday = (6 - saturdayStart.getDay() + 7) % 7
            saturdayStart.setDate(saturdayStart.getDate() + daysUntilSaturday)

            const saturdayRange = getOpenCloseMinutes(saturdayStart, locationInfo?.schedule)
            const saturdayOpenMinutes = saturdayRange?.open ?? 8 * 60
            saturdayStart.setHours(Math.floor(saturdayOpenMinutes / 60), saturdayOpenMinutes % 60, 0, 0)

            if (saturdayStart.getTime() <= now.getTime()) {
                saturdayStart.setDate(saturdayStart.getDate() + 7)
            }

            const sundayEnd = new Date(saturdayStart)
            sundayEnd.setDate(sundayEnd.getDate() + 1)
            const sundayRange = getOpenCloseMinutes(sundayEnd, locationInfo?.schedule)
            const sundayOpenMinutes = sundayRange?.open ?? 8 * 60
            sundayEnd.setHours(Math.floor(sundayOpenMinutes / 60), sundayOpenMinutes % 60, 0, 0)

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod({
                from: `${formatDate(saturdayStart)}T${minutesToTime(saturdayOpenMinutes)}`,
                to: `${formatDate(sundayEnd)}T${minutesToTime(sundayOpenMinutes)}`
            })
            return
        }

        if (isHolidayTariff(enabledFor)) {
            const defaultOpenMinutes = 8 * 60
            const now = new Date()
            const today = stripTime(now)
            const holidayRange = getNextHolidayTariffRange(categoryOption, today)
            if (!holidayRange) return

            let startDate = holidayRange.from
            if (today.getTime() >= holidayRange.from.getTime() && today.getTime() <= holidayRange.until.getTime()) {
                startDate = today
            }

            let startRange = getOpenCloseMinutes(startDate, locationInfo?.schedule)
            if (!startRange) {
                const maxAttempts = Math.max(1, Math.round((holidayRange.until.getTime() - startDate.getTime()) / 86400000))
                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    const candidate = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + attempt
                    )
                    if (candidate.getTime() > holidayRange.until.getTime()) break

                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo?.schedule)
                    if (candidateRange) {
                        startDate = stripTime(candidate)
                        startRange = candidateRange
                        break
                    }
                }
            }

            const holidayDurationDays = getCategoryOptionDurationInDays(categoryOption) || 1
            const endDate = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate() + holidayDurationDays
            )
            const endRange = getOpenCloseMinutes(endDate, locationInfo?.schedule)

            const startOpenMinutes = startRange ? startRange.open : defaultOpenMinutes
            const endOpenMinutes = endRange ? endRange.open : startOpenMinutes
            const nextFrom = `${formatDate(startDate)}T${minutesToTime(startOpenMinutes)}`
            const nextTo = `${formatDate(endDate)}T${minutesToTime(endOpenMinutes)}`

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod((prev) => {
                if (prev?.from === nextFrom && prev?.to === nextTo) return prev
                return {from: nextFrom, to: nextTo}
            })
            return
        }

        if (isAllWeekMultiDayDuration) {
            const defaultOpenMinutes = 8 * 60
            let startDate = getStartDateAfterHolidayRanges(
                getManualCategorySelectionStartDate(),
                allWeekDurationDays
            )
            let startRange = getOpenCloseMinutes(startDate, locationInfo?.schedule)

            if (!startRange) {
                for (let attempt = 1; attempt <= 14; attempt += 1) {
                    const candidate = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + attempt
                    )
                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo?.schedule)
                    if (candidateRange) {
                        startDate = stripTime(candidate)
                        startRange = candidateRange
                        break
                    }
                }
            }

            let endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + allWeekDurationDays)
            let endRange = getOpenCloseMinutes(endDate, locationInfo?.schedule)

            const startOpenMinutes = startRange ? startRange.open : defaultOpenMinutes
            const endOpenMinutes = endRange ? endRange.open : startOpenMinutes
            const nextFrom = `${formatDate(startDate)}T${minutesToTime(startOpenMinutes)}`
            const nextTo = `${formatDate(endDate)}T${minutesToTime(endOpenMinutes)}`

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod((prev) => {
                if (prev?.from === nextFrom && prev?.to === nextTo) return prev
                return {from: nextFrom, to: nextTo}
            })
            return
        }

        if (isAllWeekLongDuration) {
            const defaultOpenMinutes = 8 * 60
            let startDate = getStartDateAfterHolidayRanges(
                getManualCategorySelectionStartDate(),
                allWeekDurationDays
            )
            let startRange = getOpenCloseMinutes(startDate, locationInfo?.schedule)

            if (!startRange) {
                for (let attempt = 1; attempt <= 14; attempt += 1) {
                    const candidate = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + attempt
                    )
                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo?.schedule)
                    if (candidateRange) {
                        startDate = stripTime(candidate)
                        startRange = candidateRange
                        break
                    }
                }
            }

            let endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + allWeekDurationDays)
            let endRange = getOpenCloseMinutes(endDate, locationInfo?.schedule)

            const startOpenMinutes = startRange ? startRange.open : defaultOpenMinutes
            const endOpenMinutes = endRange ? endRange.open : startOpenMinutes
            const nextFrom = `${formatDate(startDate)}T${minutesToTime(startOpenMinutes)}`
            const nextTo = `${formatDate(endDate)}T${minutesToTime(endOpenMinutes)}`

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod((prev) => {
                if (prev?.from === nextFrom && prev?.to === nextTo) return prev
                return {from: nextFrom, to: nextTo}
            })
            return
        }

        if (isOneWeekDuration) {
            const currentStart = rentalPeriod?.from ? new Date(rentalPeriod.from) : null
            const currentEnd = rentalPeriod?.to ? new Date(rentalPeriod.to) : null
            const hasValidCurrentRange = Boolean(
                currentStart &&
                currentEnd &&
                !Number.isNaN(currentStart.getTime()) &&
                !Number.isNaN(currentEnd.getTime())
            )
            const currentRangeMinutes = hasValidCurrentRange
                ? Math.max(0, Math.round((currentEnd.getTime() - currentStart.getTime()) / 60000))
                : 0
            const oneWeekMinutes = 7 * 24 * 60

            // If user already selected a weekly range, keep their dates/times unchanged
            // and only switch tariff, unless that range overlaps a holiday tariff.
            if (currentRangeMinutes >= oneWeekMinutes) {
                const shiftedStartDate = getStartDateAfterHolidayRanges(currentStart, 7)
                const normalizedCurrentStart = stripTime(currentStart)
                if (
                    normalizedCurrentStart &&
                    shiftedStartDate.getTime() === normalizedCurrentStart.getTime()
                ) {
                    return
                }

                const shiftedEndDate = new Date(
                    shiftedStartDate.getFullYear(),
                    shiftedStartDate.getMonth(),
                    shiftedStartDate.getDate() + 7
                )
                const shiftedStartRange = getOpenCloseMinutes(shiftedStartDate, locationInfo?.schedule)
                const shiftedEndRange = getOpenCloseMinutes(shiftedEndDate, locationInfo?.schedule)
                const defaultOpenMinutes = 8 * 60
                const shiftedStartOpenMinutes = shiftedStartRange ? shiftedStartRange.open : defaultOpenMinutes
                const shiftedEndOpenMinutes = shiftedEndRange ? shiftedEndRange.open : defaultOpenMinutes

                isUpdatingPeriodFromCategoryRef.current = true
                setRentalPeriod({
                    from: `${formatDate(shiftedStartDate)}T${minutesToTime(shiftedStartOpenMinutes)}`,
                    to: `${formatDate(shiftedEndDate)}T${minutesToTime(shiftedEndOpenMinutes)}`
                })
                return
            }

            const startDate = getStartDateAfterHolidayRanges(getManualCategorySelectionStartDate(), 7)
            const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7)

            const startRange = getOpenCloseMinutes(startDate, locationInfo?.schedule)
            const endRange = getOpenCloseMinutes(endDate, locationInfo?.schedule)
            const defaultOpenMinutes = 8 * 60

            const startOpenMinutes = startRange ? startRange.open : defaultOpenMinutes
            const endOpenMinutes = endRange ? endRange.open : defaultOpenMinutes

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod({
                from: `${formatDate(startDate)}T${minutesToTime(startOpenMinutes)}`,
                to: `${formatDate(endDate)}T${minutesToTime(endOpenMinutes)}`
            })
            return
        }

        const isOneDayDuration = durationValue === 1 && durationUnit === "day"

        if (isOneDayDuration) {
            const defaultOpenMinutes = 8 * 60
            let startDate = getStartDateAfterHolidayRanges(getManualCategorySelectionStartDate(), 1)
            let startRange = getOpenCloseMinutes(startDate, locationInfo?.schedule)

            if (!startRange) {
                for (let attempt = 1; attempt <= 14; attempt += 1) {
                    const candidate = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + attempt
                    )
                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo?.schedule)
                    if (candidateRange) {
                        startDate = stripTime(candidate)
                        startRange = candidateRange
                        break
                    }
                }
            }

            let endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1)
            let endRange = getOpenCloseMinutes(endDate, locationInfo?.schedule)

            if (!endRange) {
                for (let attempt = 1; attempt <= 14; attempt += 1) {
                    const candidate = new Date(
                        endDate.getFullYear(),
                        endDate.getMonth(),
                        endDate.getDate() + attempt
                    )
                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo?.schedule)
                    if (candidateRange) {
                        endDate = stripTime(candidate)
                        endRange = candidateRange
                        break
                    }
                }
            }

            const startOpenMinutes = startRange ? startRange.open : defaultOpenMinutes
            const endOpenMinutes = endRange ? endRange.open : startOpenMinutes
            const nextFrom = `${formatDate(startDate)}T${minutesToTime(startOpenMinutes)}`
            const nextTo = `${formatDate(endDate)}T${minutesToTime(endOpenMinutes)}`

            isUpdatingPeriodFromCategoryRef.current = true
            setRentalPeriod((prev) => {
                if (prev?.from === nextFrom && prev?.to === nextTo) return prev
                return {from: nextFrom, to: nextTo}
            })
        }
    }, [locationInfo, selectedCategoryOptionId, expectedKm])

    const handlePriceLoadingChange = useCallback((loading) => {
        const normalizedLoading = Boolean(loading)
        setIsDailyCategoryOptionLoading((prev) => (prev === normalizedLoading ? prev : normalizedLoading))
        if (!normalizedLoading) {
            setDailyCategoryOptionLoadingId(null)
        }
    }, [])

    useEffect(() => {
        if (!vehicleInfo || !locationInfo) return
        if (initialPeriodSetRef.current) return

        const step = normalizeStep(vehicleInfo?.rental_config?.rental_step)
        const now = new Date()

        if (isDailyRental) {
            if (vehicleInfo?.white_label_overrides?.rental_config === "privateshare_daily") {
                setRentalPeriod(buildPrivateShareDefaultRentalPeriod(now))
                initialPeriodSetRef.current = true
                return
            }

            const defaultOpenMinutes = 8 * 60
            let startDate = stripTime(now)
            let startRange = getOpenCloseMinutes(startDate, locationInfo.schedule)
            if (!startRange) {
                for (let attempt = 1; attempt <= 14; attempt += 1) {
                    const candidate = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + attempt
                    )
                    const candidateRange = getOpenCloseMinutes(candidate, locationInfo.schedule)
                    if (candidateRange) {
                        startDate = stripTime(candidate)
                        startRange = candidateRange
                        break
                    }
                }
            }
            const startMinutes = startRange ? startRange.open : defaultOpenMinutes

            const startDateTime = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                Math.floor(startMinutes / 60),
                startMinutes % 60
            )
            const endDateTime = new Date(startDateTime.getTime() + 24 * 60 * 60 * 1000)

            const nextFrom = `${formatDate(startDate)}T${minutesToTime(startMinutes)}`
            const nextTo = `${formatDate(endDateTime)}T${minutesToTime(endDateTime.getHours() * 60 + endDateTime.getMinutes())}`
            setRentalPeriod({from: nextFrom, to: nextTo})
            initialPeriodSetRef.current = true
            return
        }

        let baseDate = stripTime(now)
        let minutesNow = now.getHours() * 60 + now.getMinutes()
        minutesNow = roundDownToStep(minutesNow, step)

        const useHourlyPickerMode = useCurrentTimeDefault
        let range = useHourlyPickerMode ? {open: 0, close: 24 * 60} : getOpenCloseMinutes(baseDate, locationInfo.schedule)
        if (!useHourlyPickerMode && !range) {
            for (let attempt = 1; attempt <= 14; attempt += 1) {
                const candidate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + attempt)
                const candidateRange = getOpenCloseMinutes(candidate, locationInfo.schedule)
                if (candidateRange) {
                    baseDate = stripTime(candidate)
                    range = candidateRange
                    break
                }
            }
        }

        if (range) {
            const isOpen24h = range.open === 0 && range.close === 24 * 60
            if (isOpen24h || useHourlyPickerMode) {
                minutesNow = roundDownToStep(minutesNow, step)
            } else {
                minutesNow = range.open
            }
        }

        const minConfig = vehicleInfo?.rental_config?.minimum_rental_time
        const minMinutes = minConfig?.unit === 'day'
            ? (minConfig.value || 1) * 24 * 60
            : minConfig?.unit === 'hour'
                ? (minConfig.value || 1) * 60
                : minConfig?.unit === 'minute'
                    ? (minConfig.value || 1)
                    : 24 * 60

        const startDateTime = new Date(
            baseDate.getFullYear(),
            baseDate.getMonth(),
            baseDate.getDate(),
            Math.floor(minutesNow / 60),
            minutesNow % 60
        )
        const endDateTime = new Date(startDateTime.getTime() + minMinutes * 60 * 1000)
        const isOpen24h = Boolean(range && range.open === 0 && range.close === 24 * 60)
        const adjustedEnd = useHourlyPickerMode
            ? (() => {
                const roundedMinutes = roundUpToStep(endDateTime.getHours() * 60 + endDateTime.getMinutes(), step)
                const extraDays = Math.floor(roundedMinutes / (24 * 60))
                const safeMinutes = roundedMinutes % (24 * 60)
                const date = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate() + extraDays)
                return {date: stripTime(date), minutes: safeMinutes}
            })()
            : (isOpen24h
                ? ensureWithinSchedule(endDateTime, locationInfo.schedule, step, 'up')
                : ensureOpenDay(endDateTime, locationInfo.schedule))

        const nextFrom = `${formatDate(baseDate)}T${minutesToTime(minutesNow)}`
        const nextTo = `${formatDate(adjustedEnd.date)}T${minutesToTime(adjustedEnd.minutes)}`
        setRentalPeriod({from: nextFrom, to: nextTo})
        initialPeriodSetRef.current = true
    }, [vehicleInfo, locationInfo, useCurrentTimeDefault, isDailyRental])

    const isRentalValid = useMemo(() => {
        return Boolean(rentalPeriod.from && rentalPeriod.to)
    }, [rentalPeriod.from, rentalPeriod.to])
    const isDailyRentalLongerThanSupportedRange = useMemo(() => {
        if (!shouldShowDailyCategoryOptions) return false
        if (!rentalPeriod.from || !rentalPeriod.to) return false

        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false

        const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
        return diffMinutes > (maxSupportedDailyRangeDays * 24 * 60)
    }, [shouldShowDailyCategoryOptions, rentalPeriod.from, rentalPeriod.to, maxSupportedDailyRangeDays])
    const isExpectedKmFilled = useMemo(() => {
        return String(expectedKm || "").trim() !== ""
    }, [expectedKm])
    const isContinueBlocked = useMemo(() => {
        if (!isRentalValid) return true
        if (isDailyRentalLongerThanSupportedRange) return true
        if ((useCurrentTimeDefault || isPrivateShareTheme) && !isExpectedKmFilled) return true
        return false
    }, [isRentalValid, isDailyRentalLongerThanSupportedRange, useCurrentTimeDefault, isPrivateShareTheme, isExpectedKmFilled])

    useEffect(() => {
        if (!isDailyCategoryOptionLoading) return
        if (!isDailyRentalLongerThanSupportedRange) return
        setIsDailyCategoryOptionLoading(false)
        setDailyCategoryOptionLoadingId(null)
    }, [isDailyCategoryOptionLoading, isDailyRentalLongerThanSupportedRange])

    useEffect(() => {
        if (isExpectedKmFilled && showDailyKmHighlight) {
            setShowDailyKmHighlight(false)
        }
    }, [isExpectedKmFilled, showDailyKmHighlight])

    useEffect(() => {
        if (availabilityInlineMessage) {
            setAvailabilityInlineMessage("")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rentalPeriod.from, rentalPeriod.to])

    const minRentalText = useMemo(() => {
        const minConfig = vehicleInfo?.rental_config?.minimum_rental_time
        if (!minConfig || !minConfig.value) return ""
        const value = minConfig.value
        if (minConfig.unit === 'day') {
            const label = value === 1 ? 'dag' : 'dagen'
            return `Minimale kosten: huurprijs voor ${value} ${label}.`
        }
        if (minConfig.unit === 'hour') {
            const label = value === 1 ? 'uur' : 'uur'
            return `Minimale kosten: huurprijs voor ${value} ${label}.`
        }
        if (minConfig.unit === 'minute') {
            const label = value === 1 ? 'minuut' : 'minuten'
            return `Minimale kosten: huurprijs voor ${value} ${label}.`
        }
        return ""
    }, [vehicleInfo])

    const minRentalMinutes = useMemo(() => {
        const minConfig = vehicleInfo?.rental_config?.minimum_rental_time
        if (!minConfig) return 24 * 60
        const value = Number(minConfig.value) || 0
        if (minConfig.unit === 'day') return value * 24 * 60
        if (minConfig.unit === 'hour') return value * 60
        if (minConfig.unit === 'minute') return value
        return 24 * 60
    }, [vehicleInfo])

    const isBelowMinRental = useMemo(() => {
        const minConfig = vehicleInfo?.rental_config?.minimum_rental_time
        if (!minConfig || !rentalPeriod.from || !rentalPeriod.to) return false
        const minMinutes = minConfig.unit === 'day'
            ? minConfig.value * 24 * 60
            : minConfig.unit === 'hour'
                ? minConfig.value * 60
                : minConfig.unit === 'minute'
                    ? minConfig.value
                    : 0
        if (!minMinutes) return false
        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
        const diffMinutes = (end.getTime() - start.getTime()) / 60000
        return diffMinutes < minMinutes
    }, [vehicleInfo, rentalPeriod.from, rentalPeriod.to])

    const showMinRentalWarning = Boolean(
        minRentalText && rentalPeriod.from && rentalPeriod.to && isBelowMinRental
    )

    const hasVehicleExtras = useMemo(() => {
        if (!vehicleInfo) return true
        const hasOptions = Array.isArray(vehicleInfo.options) && vehicleInfo.options.length > 0
        const hasProducts = Array.isArray(vehicleInfo.products) && vehicleInfo.products.length > 0
        return hasOptions || hasProducts
    }, [vehicleInfo])

    const stepperSteps = hasVehicleExtras
        ? ['Huurperiode', 'Opties', 'Je gegevens']
        : ['Huurperiode', 'Je gegevens']

    // Equivalent vehicles have the same options and products, so the next step is the same after a swap.
    const buildNextPathname = (targetVehicleId) => hasVehicleExtras
        ? `/reservation-options/${targetVehicleId}/${locationId}`
        : `/reservation-confirm/${targetVehicleId}/${locationId}`
    const nextPathname = buildNextPathname(vehicleId)

    const nextSearch = useMemo(() => {
        return appendPublicWrapperBaseToSearch(new URLSearchParams({
            from: rentalPeriod.from || "",
            to: rentalPeriod.to || "",
            expectedKm: expectedKm ?? "",
        }), location.search).toString()
    }, [rentalPeriod.from, rentalPeriod.to, expectedKm, location.search])

    const handleContinueClick = async (event) => {
        event.preventDefault()

        if (isAvailabilityChecking) return

        if (!isRentalValid) {
            return
        }

        if (isDailyRentalLongerThanSupportedRange) {
            return
        }

        if ((useCurrentTimeDefault || isPrivateShareTheme) && !isExpectedKmFilled) {
            setShowDailyKmHighlight(true)
            setShowExpectedKmModal(true)
            return
        }

        if (!rentalPeriod.from || !rentalPeriod.to) return

        setIsAvailabilityChecking(true)
        setAvailabilityInlineMessage("")

        try {
            // All equivalent vehicles on the location; a busy vehicle is replaced by a free one.
            const bookable = await resolveBookableVehicle({
                locationId,
                vehicleId,
                startDate: rentalPeriod.from,
                endDate: rentalPeriod.to,
            })

            if (bookable.status === "free" || bookable.status === "swapped") {
                if (bookable.status === "swapped") {
                    sendEvent("reserve_vehicle_swapped")
                }
                navigate({
                    pathname: buildNextPathname(bookable.vehicleId),
                    search: nextSearch,
                })
                return
            }

            if (bookable.status === "unavailable") {
                sendEvent("reserve_period_unavailable")
                const message = buildUnavailableMessage(bookable.vehicles)
                const hasEquivalents = bookable.vehicles.length > 1
                const inlineLines = buildOccupancyLines(bookable.vehicles, {
                    withPlate: hasEquivalents,
                    separator: ": ",
                    formatRange: (start, end) => {
                        const from = formatDateTimeShortNl(start)
                        const to = formatDateTimeShortNl(end)
                        return from && to ? `${from} - ${to}` : "bezet"
                    },
                })

                setAvailabilityModal(message)
                setAvailabilityInlineMessage(inlineLines.length > 0
                    ? inlineLines.join('\n')
                    : "Voertuig niet beschikbaar in de gekozen periode.")
                setShowAvailabilityModal(true)
                return
            }

            // Older API without the location check: only this vehicle.
            const availabilityResponse = await getVehicleAvailabilityAPI({
                vehicleId,
                startDate: rentalPeriod.from,
                endDate: rentalPeriod.to,
            })

            if (availabilityResponse?.can_rent) {
                navigate({
                    pathname: nextPathname,
                    search: nextSearch,
                })
                return
            }

            sendEvent("reserve_period_unavailable")
            const occupiedRanges = getOccupiedRanges(availabilityResponse?.occupied_at)
            const fullRanges = occupiedRanges
                .map((range) => {
                    const start = formatDateTimeFullNl(range.start)
                    const end = formatDateTimeFullNl(range.end)
                    if (start && end) return `${start} tot ${end}`
                    return null
                })
                .filter(Boolean)
            const shortRanges = occupiedRanges
                .map((range) => {
                    const start = formatDateTimeShortNl(range.start)
                    const end = formatDateTimeShortNl(range.end)
                    if (start && end) return `${start} - ${end}`
                    return null
                })
                .filter(Boolean)

            const modalMessage = fullRanges.length > 0
                ? `Dit voertuig is bezet van ${fullRanges.join('; ')}. Kies alstublieft een andere tijd.`
                : "Dit voertuig is niet beschikbaar in de gekozen periode. Kies alstublieft een andere tijd."
            const inlineMessage = shortRanges.length > 0
                ? shortRanges.join('\n')
                : "Voertuig niet beschikbaar in de gekozen periode."

            setAvailabilityModal({title: "Voertuig niet beschikbaar", intro: modalMessage, lines: [], outro: ""})
            setAvailabilityInlineMessage(inlineMessage)
            setShowAvailabilityModal(true)
        } catch (error) {
            setAvailabilityModal({
                title: "Voertuig niet beschikbaar",
                intro: "Beschikbaarheid kon niet worden gecontroleerd. Probeer het opnieuw.",
                lines: [],
                outro: "",
            })
            setShowAvailabilityModal(true)
        } finally {
            setIsAvailabilityChecking(false)
        }
    }

    const extraDayWarningText = useMemo(() => {
        if (pricingMeta.specialPriceType) return ""
        const minRentalUnit = vehicleInfo?.rental_config?.minimum_rental_time?.unit
        if (minRentalUnit !== 'day') return ""
        if (!locationInfo?.schedule) return ""
        if (!rentalPeriod.from || !rentalPeriod.to) return ""
        const start = new Date(rentalPeriod.from)
        const end = new Date(rentalPeriod.to)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ""
        const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate())
        const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate())
        if (endDate.getTime() <= startDate.getTime()) return ""
        const dayIndex = end.getDay() === 0 ? 7 : end.getDay()
        const entry = locationInfo.schedule.find((item) => item.dayOfWeek === dayIndex)
        if (!entry || entry.isClosed || entry.isOpen24h) return ""
        if (entry.openHour === null || entry.openHour === undefined) return ""
        const openMinutes = entry.openHour * 60 + (entry.openMinute || 0)
        const endMinutes = end.getHours() * 60 + end.getMinutes()
        if (endMinutes > openMinutes) {
            return `Let op! Je hebt een tijd na ${minutesToTime(openMinutes)} uur geselecteerd. Dit betekent dat je een extra dag moet reserveren.`
        }
        return ""
    }, [pricingMeta.specialPriceType, locationInfo, rentalPeriod.from, rentalPeriod.to, vehicleInfo?.rental_config?.minimum_rental_time?.unit])

    const weekendTariffRangeText = useMemo(() => {
        const toTime = (minutes) => {
            const safeMinutes = Math.max(0, Math.min((24 * 60) - 1, Number(minutes) || 0))
            const hour = String(Math.floor(safeMinutes / 60)).padStart(2, '0')
            const minute = String(safeMinutes % 60).padStart(2, '0')
            return `${hour}:${minute}`
        }

        const fallbackText = `zaterdag ${toTime(DEFAULT_WEEKEND_START_MINUTES)} tot maandag ${toTime(DEFAULT_WEEKEND_END_MINUTES)}`
        if (!Array.isArray(locationInfo?.schedule) || locationInfo.schedule.length === 0) return fallbackText

        const saturdayEntry = locationInfo.schedule.find((item) => Number(item?.dayOfWeek) === 6)
        const mondayEntry = locationInfo.schedule.find((item) => Number(item?.dayOfWeek) === 1)

        const hasSaturdayOpen = Boolean(
            saturdayEntry &&
            !saturdayEntry.isClosed &&
            !saturdayEntry.isOpen24h &&
            saturdayEntry.openHour !== null &&
            saturdayEntry.openHour !== undefined
        )
        const hasMondayOpen = Boolean(
            mondayEntry &&
            !mondayEntry.isClosed &&
            !mondayEntry.isOpen24h &&
            mondayEntry.openHour !== null &&
            mondayEntry.openHour !== undefined
        )

        const saturdayOpenMinutes = hasSaturdayOpen
            ? (Number(saturdayEntry.openHour) * 60) + Number(saturdayEntry.openMinute || 0)
            : DEFAULT_WEEKEND_START_MINUTES
        const mondayOpenMinutes = hasMondayOpen
            ? (Number(mondayEntry.openHour) * 60) + Number(mondayEntry.openMinute || 0)
            : DEFAULT_WEEKEND_END_MINUTES

        return `zaterdag ${toTime(saturdayOpenMinutes)} tot maandag ${toTime(mondayOpenMinutes)}`
    }, [locationInfo?.schedule])

    const specialPriceWarningText = useMemo(() => {
        if (!pricingMeta.specialPriceType) return ""

        const getOpeningMinutesForWeekday = (dayOfWeek, fallbackMinutes = DEFAULT_WEEKEND_START_MINUTES) => {
            const entry = Array.isArray(locationInfo?.schedule)
                ? locationInfo.schedule.find((item) => Number(item?.dayOfWeek) === dayOfWeek)
                : null

            if (!entry || entry.isClosed) return fallbackMinutes
            if (entry.isOpen24h) return 0
            if (entry.openHour === null || entry.openHour === undefined) return fallbackMinutes

            return (Number(entry.openHour) * 60) + Number(entry.openMinute || 0)
        }

        const saturdayOpenMinutes = getOpeningMinutesForWeekday(6)
        const sundayOpenMinutes = getOpeningMinutesForWeekday(7)

        const saturdayOpenText = Number.isFinite(saturdayOpenMinutes) ? minutesToTime(saturdayOpenMinutes) : "--:--"
        const sundayOpenText = Number.isFinite(sundayOpenMinutes) ? minutesToTime(sundayOpenMinutes) : "--:--"
        const priceText = pricingMeta.vehicleRentPrice !== null
            ? formatPriceNl(pricingMeta.vehicleRentPrice).replace(",-", "")
            : "--"

        if (pricingMeta.isWeekendKb) {
            return `Let op: de gekozen huurtijd valt binnen het weekendtarief. Huur van ${weekendTariffRangeText} wordt berekend tegen een vaste prijs van ${priceText} euro.`
        }

        if (pricingMeta.isSaturdayKb) {
            return `Let op: de gekozen huurtijd valt binnen het zaterdagtarief. De huurprijs op zaterdag van ${saturdayOpenText} tot zondag ${sundayOpenText} is vast — ${priceText} euro.`
        }

        if (!pricingMeta.isHoliday) return ""
        const holidayName = pricingMeta.holidayTariffName || "feestdagtarief"
        return `Let op: de gekozen huurtijd valt binnen het ${holidayName}. De huurprijs is vast — ${priceText} euro.`
    }, [
        pricingMeta.specialPriceType,
        pricingMeta.isWeekendKb,
        pricingMeta.isSaturdayKb,
        pricingMeta.isHoliday,
        pricingMeta.holidayTariffName,
        pricingMeta.vehicleRentPrice,
        locationInfo?.schedule,
        weekendTariffRangeText
    ])

    const warningText = extraDayWarningText || minRentalText
    const showWarning = Boolean(extraDayWarningText || (!pricingMeta.specialPriceType && showMinRentalWarning))
    const handlePriceVatPreferenceChange = (nextShowPricesInclVat) => {
        setShowPricesInclVat(nextShowPricesInclVat)
        setPriceVatPreference(nextShowPricesInclVat)
    }

    return (
        <>
            <Header/>

            <Devider />

            <LocationLogoHeader locationInfo={locationInfo} vehicleList={[vehicleInfo]} />

            <div className="container mt-5 kav-reservation-top">
                {
                    isDemoReservationLocation ? (
                        <div className="kav-reservation-top__demoLogoWrap" aria-hidden="true">
                            <img src={demoLogo} alt="" className="kav-reservation-top__demoLogo"/>
                        </div>
                    ) : null
                }
                <div className="row">
                    <div className="col-md-4 front-reservation-promo">
                        <h1>Reserveren</h1>
                        {/*<p>Binnen 1 minuut in {stepperSteps.length} eenvoudige stappen.</p>*/}
                    </div>
                    <div className="col-md-4">
                        <Stepper steps={stepperSteps} current={0}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <ReservationStepNotice step="Stap 1">
                            Kies je huurperiode
                        </ReservationStepNotice>
                    </div>
                </div>
                {
                    isDemoReservationLocation ? (
                        <div className="row">
                            <div className="col-12">
                                <div className="kav-demo-step-banner">
                                    <span className="kav-demo-step-banner__badge">Stap 2</span>
                                    <p>Waar en wanneer reserveert u het voertuig</p>
                                </div>
                            </div>
                        </div>
                    ) : null
                }
            </div>

            <div className="container mt-5 mb-5">
                {locationInfo && vehicleInfo ? (
                    <div className="row">
                        <div className="col-12">
                            <PriceVatToggle
                                showPricesInclVat={showPricesInclVat}
                                onChange={handlePriceVatPreferenceChange}
                            />
                        </div>
                        <div className="col-md-6">
                            <ReservationPanel
                                locationId={locationId}
                                timeStepMinutes={vehicleInfo.rental_config.rental_step}
                                locationInfo={locationInfo}
                                rentalPeriod={rentalPeriod}
                                setRentalPeriod={setRentalPeriod}
                                expectedKm={expectedKm}
                                setExpectedKm={setExpectedKm}
                                showExpectedKmField={shouldShowExpectedKmField}
                                showDailyCategoryOptions={shouldShowDailyCategoryOptions}
                                dailyCategoryOptions={dailyCategoryOptions}
                                selectedCategoryOptionId={selectedCategoryOptionId}
                                onDailyCategoryOptionSelect={handleDailyCategoryOptionSelect}
                                isDailyCategoryOptionLoading={isDailyCategoryOptionLoading}
                                dailyCategoryOptionLoadingId={dailyCategoryOptionLoadingId}
                                highlightExpectedKm={showDailyKmHighlight}
                                minRentalText={warningText}
                                showMinRentalWarning={showWarning}
                                tariffWarningText={specialPriceWarningText}
                                minRentalMinutes={minRentalMinutes}
                                useCurrentTimeDefault={useCurrentTimeDefault}
                                isDailyRental={isDailyRental}
                                availabilityConflictText={availabilityInlineMessage}
                                pricePerExtraKilometer={pricingMeta.pricePerExtraKilometer}
                                eigenRisk={pricingMeta.eigenRisk}
                                showPricesInclVat={showPricesInclVat}
                                subsidiaryId={resolveWhiteLabelSubsidiaryId(
                                    vehicleInfo.company?.parent?.id ?? vehicleInfo.company?.id
                                )}
                            />

                            <div className="kav-step-actions">
                                <div className="kav-step-actions__row">
                                    <NavLink
                                        to={`/locations/${locationId}/${vehicleId}`}
                                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                    >
                                        Terug
                                    </NavLink>
                                    <div className="kav-step-actions__next">
                                        <NavLink
                                            to={{pathname: nextPathname, search: nextSearch}}
                                            className={`kav-step-actions__btn kav-step-actions__btn--primary ${isContinueBlocked ? 'is-blocked' : ''}`}
                                            aria-disabled={isContinueBlocked || isAvailabilityChecking}
                                            onClick={handleContinueClick}
                                            tabIndex={0}
                                        >
                                            {isAvailabilityChecking ? (
                                                <>
                                                    <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>
                                                    Controleren...
                                                </>
                                            ) : (
                                                <>
                                                    Volgende
                                                    <i className="bi bi-chevron-right" aria-hidden="true"></i>
                                                </>
                                            )}
                                        </NavLink>
                                        {/*<span className="kav-step-actions__hint">*/}
                                        {/*    {hasVehicleOptions ? 'Selecteer extra opties' : 'Vul je gegevens in'}*/}
                                        {/*</span>*/}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <ReservationVehicle
                                vehicleInfo={vehicleInfo}
                                rentalPeriod={rentalPeriod}
                                expectedKm={expectedKm}
                                minRentalText={warningText}
                                showMinRentalWarning={showWarning}
                                onPricingMetaChange={handlePricingMetaChange}
                                onPriceLoadingChange={handlePriceLoadingChange}
                                showPricesInclVat={showPricesInclVat}
                                // showLongDailySupportMessage={isDailyRentalLongerThanSupportedRange}
                                showLongDailySupportMessage={false}
                            />
                        </div>
                    </div>
                ) : (
                    <ReservationInitLoader />
                )}
            </div>

            {showExpectedKmModal ? (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        aria-label="Sluit melding"
                        onClick={() => setShowExpectedKmModal(false)}
                    />
                    <div className="kav-simple-modal__card">
                        <h3>Geschatte afstand (VERPLICHT) invoeren</h3>
                        <p>Voer de geschatte reisafstand in — de uiteindelijke prijs wordt berekend op basis van het daadwerkelijk gereden aantal kilometers.</p>
                        <button
                            type="button"
                            style={{justifyContent: 'center'}}
                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                            onClick={() => setShowExpectedKmModal(false)}
                        >
                            Begrepen
                        </button>
                    </div>
                </div>
            ) : null}
            {showAvailabilityModal ? (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        aria-label="Sluit melding"
                        onClick={() => setShowAvailabilityModal(false)}
                    />
                    <div className="kav-simple-modal__card">
                        <h3>{availabilityModal.title || "Voertuig niet beschikbaar"}</h3>
                        {availabilityModal.intro ? <p>{availabilityModal.intro}</p> : null}
                        {availabilityModal.lines.length > 0 ? (
                            <ul className="kav-availability-modal__list">
                                {availabilityModal.lines.map((line, index) => (
                                    <li key={`${index}-${line}`}>{line}</li>
                                ))}
                            </ul>
                        ) : null}
                        {availabilityModal.outro ? <p>{availabilityModal.outro}</p> : null}
                        <button
                            type="button"
                            style={{justifyContent: 'center'}}
                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                            onClick={() => setShowAvailabilityModal(false)}
                        >
                            Sluiten
                        </button>
                    </div>
                </div>
            ) : null}
            <Footer/>
        </>
    )
}

export default ReservationInitPage
